import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ShoppingBag, Share2, Trash2, ChevronDown, ChevronUp, CalendarDays, Store, DollarSign, Copy, Check, Lock, Loader2 } from 'lucide-react';
import { useAppStore } from '@/hooks/useAppStore';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import { ROUTE_PATHS, SavedTrip } from '@/lib/index';
import { springPresets, staggerContainer, staggerItem } from '@/lib/motion';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-CA', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
  });
}

function buildTripText(trip: SavedTrip): string {
  const lines: string[] = [
    `🛒 FlyersFeast Grocery List — ${trip.name}`,
    `${trip.store} · ${formatDate(trip.savedAt)}`,
    `${trip.meals.length} meals · $${trip.totalCost.toFixed(2)} est. total`,
    '',
  ];
  trip.sections.forEach(sec => {
    lines.push(sec.section);
    sec.items.forEach(item => {
      const price = item.price > 0 ? ` — $${item.price.toFixed(2)}` : '';
      lines.push(`  ☐ ${item.name}  ${item.qty}${price}`);
    });
    lines.push('');
  });
  lines.push(`Meals: ${trip.meals.join(', ')}`);
  return lines.join('\n');
}

function TripCard({ trip, onDelete }: { trip: SavedTrip; onDelete: () => void }) {
  const [expanded, setExpanded] = useState(false);
  const [copyState, setCopyState] = useState<'idle' | 'copied'>('idle');

  const handleShare = async () => {
    const text = buildTripText(trip);
    if (navigator.share) {
      try { await navigator.share({ title: trip.name, text }); return; }
      catch { /* cancelled */ }
    }
    try {
      await navigator.clipboard.writeText(text);
      setCopyState('copied');
      setTimeout(() => setCopyState('idle'), 2500);
    } catch { /* ignore */ }
  };

  const totalItems = trip.sections.reduce((s, sec) => s + sec.items.length, 0);

  return (
    <motion.div
      layout
      variants={staggerItem}
      className="bg-card border border-border rounded-2xl overflow-hidden"
    >
      {/* Card header */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0 flex-1">
            <h3 className="font-bold text-foreground text-base leading-tight">{trip.name}</h3>
            <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><Store className="w-3 h-3" /> {trip.store}</span>
              <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3" /> {formatDate(trip.savedAt)}</span>
              <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" /> ${trip.totalCost.toFixed(2)} total</span>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={handleShare}
              className="flex items-center gap-1 text-xs font-semibold bg-primary text-primary-foreground px-2.5 py-1.5 rounded-full hover:opacity-90 transition-opacity"
            >
              {copyState === 'copied'
                ? <><Check className="w-3 h-3" /> Copied</>
                : <><Share2 className="w-3 h-3" /> Share</>
              }
            </button>
            <button
              onClick={onDelete}
              className="p-1.5 text-muted-foreground hover:text-red-500 transition-colors rounded-full hover:bg-red-50"
              title="Delete this list"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Meal chips */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {trip.meals.map((m, i) => (
            <span key={i} className="text-[10px] font-medium bg-primary/10 text-primary rounded-full px-2 py-0.5">
              {m}
            </span>
          ))}
        </div>

        {/* Expand toggle */}
        <button
          onClick={() => setExpanded(v => !v)}
          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          {expanded ? 'Hide' : `Show`} {totalItems} items
        </button>
      </div>

      {/* Expandable items */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={springPresets.gentle}
            className="overflow-hidden border-t border-border/50"
          >
            {trip.sections.map(sec => (
              <div key={sec.section}>
                <div className="px-4 py-2 bg-muted/30 flex items-center gap-2">
                  <span className="text-sm">{sec.section.split(' ')[0]}</span>
                  <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                    {sec.section.replace(/^[^\s]+\s/, '')}
                  </span>
                </div>
                {sec.items.map((item, i) => (
                  <div key={i} className="flex items-center justify-between px-4 py-2.5 border-b border-border/30 last:border-0">
                    <div className="min-w-0 flex-1">
                      <span className="text-sm text-foreground">{item.name}</span>
                      {item.qty && <span className="text-xs text-muted-foreground ml-2">{item.qty}</span>}
                    </div>
                    <span className="text-sm font-semibold tabular-nums text-foreground shrink-0">
                      {item.price > 0 ? `$${item.price.toFixed(2)}` : <span className="text-xs text-muted-foreground font-normal">pantry</span>}
                    </span>
                  </div>
                ))}
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function SavedListsPage() {
  const { savedTrips, deleteTrip, setTrips } = useAppStore();
  const { user, setShowAuthModal } = useAuth();
  const navigate = useNavigate();
  const [syncing, setSyncing] = useState(false);

  // On login: push any local-only trips to Supabase, then pull all from Supabase as source of truth
  useEffect(() => {
    if (!user) return;
    setSyncing(true);

    const sync = async () => {
      // 1. Push local trips that aren't in Supabase yet (created before login)
      //    Only push trips with valid UUIDs (newly created ones use crypto.randomUUID())
      const uuidRe = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      const localToSync = savedTrips.filter(t => uuidRe.test(t.id));
      if (localToSync.length) {
        await supabase.from('user_saved_trips').upsert(
          localToSync.map(t => ({
            id:         t.id,
            user_id:    user.id,
            name:       t.name,
            store_name: t.store,
            meals:      { names: t.meals, sections: t.sections },
            total_cost: t.totalCost,
            created_at: t.savedAt,
          })),
          { onConflict: 'id' }
        ).then(({ error }) => { if (error) console.error('[FF] sync local trips:', error.message); });
      }

      // 2. Fetch all trips from Supabase (source of truth when logged in)
      const { data, error } = await supabase
        .from('user_saved_trips')
        .select('id, name, store_name, meals, total_cost, created_at')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) { console.error('[FF] load trips:', error.message); setSyncing(false); return; }

      if (data) {
        const trips: SavedTrip[] = data.map(row => ({
          id:        row.id,
          name:      row.name,
          store:     row.store_name ?? '',
          savedAt:   row.created_at,
          meals:     Array.isArray(row.meals?.names) ? row.meals.names : [],
          totalCost: Number(row.total_cost ?? 0),
          sections:  Array.isArray(row.meals?.sections) ? row.meals.sections : [],
        }));
        setTrips(trips);  // replace local store with Supabase truth
      }
      setSyncing(false);
    };

    sync();
  }, [user?.id]);

  // Handle delete — remove locally and from Supabase
  const handleDelete = async (id: string) => {
    deleteTrip(id);
    if (user) {
      await supabase.from('user_saved_trips').delete().eq('id', id).eq('user_id', user.id);
    }
  };

  // Auth gate
  if (!user) {
    return (
      <div className="min-h-screen bg-background flex flex-col pt-12">
        <header className="sticky top-12 z-10 bg-background/95 backdrop-blur border-b border-border/50">
          <div className="container mx-auto px-4 h-14 flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="text-muted-foreground hover:text-foreground">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <span className="font-bold text-foreground flex-1">Saved Lists</span>
          </div>
        </header>
        <div className="flex-1 flex flex-col items-center justify-center gap-5 p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
            <Lock className="w-8 h-8 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-foreground mb-2">Sign in to view Saved Lists</h2>
            <p className="text-muted-foreground text-sm max-w-xs">
              Your grocery lists and meal plans are saved to your account and available on any device.
            </p>
          </div>
          <button
            onClick={() => setShowAuthModal(true)}
            className="px-6 py-3 rounded-xl bg-primary text-primary-foreground font-bold text-sm hover:opacity-90 transition-opacity"
          >
            Sign In / Create Account
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-12 pb-24">
      <header className="sticky top-12 z-10 bg-background/95 backdrop-blur border-b border-border/50">
        <div className="container mx-auto px-4 h-14 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-muted-foreground hover:text-foreground">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 flex items-center gap-2">
            <span className="font-bold text-foreground">Saved Lists</span>
            <span className="text-xs text-muted-foreground ml-1">{savedTrips.length} trip{savedTrips.length !== 1 ? 's' : ''}</span>
            {syncing && <Loader2 className="w-3.5 h-3.5 text-muted-foreground animate-spin ml-1" />}
          </div>
        </div>
      </header>

      <div className="container mx-auto max-w-xl px-4 py-5">
        {savedTrips.length === 0 ? (
          <motion.div
            {...{ initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, transition: springPresets.gentle }}
            className="text-center py-16"
          >
            <ShoppingBag className="w-12 h-12 text-muted-foreground/40 mx-auto mb-4" />
            <p className="text-base font-semibold text-foreground mb-1">No saved lists yet</p>
            <p className="text-sm text-muted-foreground mb-5">
              Build your grocery list and tap <strong>Save List</strong> to keep it here.
            </p>
            <button
              onClick={() => navigate(ROUTE_PATHS.HOME)}
              className="text-primary font-semibold text-sm hover:underline"
            >
              ← Start a new meal plan
            </button>
          </motion.div>
        ) : (
          <motion.div
            variants={staggerContainer} initial="hidden" animate="visible"
            className="space-y-4"
          >
            {savedTrips.map(trip => (
              <TripCard key={trip.id} trip={trip} onDelete={() => handleDelete(trip.id)} />
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
}
